// 22.01.19
// This module will eventually contain all the styling information for all our components
// We use this method of styling our components because this is what M-UI requires so that
// we can use their themes and styles.
// Having access to the themes and styles when creating the CSS means that we can apply consist
// spacing/padding/margins as well as use colors and such that we have defined in `app-theme.tsx`
import {
  createStyles,
  Button,
  Theme,
  Grid,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import styled from "styled-components";
import {
  PRIMARY_COLOR,
  TOASTER_ERROR,
  TOASTER_SUCCESS,
} from "../constants/color";
import { PrimayBackground } from "./css";
import CloseIcon from "@material-ui/icons/Close";

const drawerWidth: number = 240;

export const generalStyles = (theme: Theme) =>
  createStyles({
    // TODO: Merge these styles
    contentPadding: {
      padding: theme.spacing(2),
    },
    minHeight: {
      minHeight: "80vh",
    },
  });

export const introStyles = (theme: Theme) => createStyles({});

export const headerStyles = (theme: Theme) => ({
  ...createStyles({
    toolbar: {
      ...theme.mixins.toolbar,
    },
    root: {
      display: "block",
    },
    appBar: {
      // width: `calc(100% - ${drawerWidth}px)`,
      // marginLeft: drawerWidth
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      fontFamily: "sans-serif",
    },
    drawerPaper: {
      width: "320px",
      paddingLeft: "24px",
      paddingRight: "24px",
      borderLeftWidth: "1px!important",
      borderStyle: "solid",
      borderWidth: "0",
      borderColor: "#c8c8c8",
      backgroundColor: "#f4f4f4",
      top: "64px",
    },
    drawerHeader: {
      paddingTop: "24px",
      color: "#333",
      fontSize: "28px",
      paddingBottom: "18px",
    },
    content: {
      flexGrow: 1,
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(3),
    },
    nestedListItem: {
      paddingLeft: theme.spacing(4),
    },
  }),
});

export const toolbarStyles = (theme: Theme) => {
  const transDuration: number = 500;
  const transTypeEnter: string = "cubic-bezier(0.165, 0.840, 0.440, 1.000)";
  const transTypeExit: string = "cubic-bezier(0.895, 0.030, 0.685, 0.220)";
  const transEnter: string = `all ${transDuration}ms ${transTypeEnter}`;
  const transExit: string = `all ${transDuration}ms ${transTypeExit}`;
  return createStyles({
    toolbarFab: {
      color: "white",
      minWidth: theme.spacing(7),
    },

    staticToolbarContainer: {
      display: "flex",
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
      paddingLeft: 0,
    },
    staticToolbarButton: {
      marginRight: theme.spacing(2),
      borderRadius: theme.spacing(2),
      whiteSpace: "nowrap",
    },
    staticToolbarDestructiveButton: {
      backgroundColor: theme.palette.error.main,
    },
    toolbarContainer: {
      position: "fixed",
      display: "flex",
      borderRadius: theme.spacing(4),
      overflow: "hidden",
      zIndex: 10,
    },
    buttonContainerEnter: {
      opacity: 0,
    },
    buttonContainerEnterActive: {
      opacity: 1,
      transition: transEnter,
    },
    buttonContainerEnterDone: {
      opacity: 1,
    },
    buttonContainerExit: {
      opacity: 1,
    },
    buttonContainerExitActive: {
      opacity: 0,
      transition: transExit,
    },
    buttonContainerExitDone: {
      opacity: 0,
    },
    toolbarContainerEnter: {
      width: theme.spacing(7),
    },
    toolbarContainerEnterActive: {
      width: "50%",
      transition: transEnter,
    },
    toolbarContainerEnterDone: {
      width: "50%",
    },
    toolbarContainerExit: {
      width: "50%",
    },
    toolbarContainerExitActive: {
      width: theme.spacing(7),
      transition: transExit,
    },
    toolbarContainerExitDone: {
      width: theme.spacing(7),
    },
    buttonContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginRight: theme.spacing(7),
    },
    toolbarButton: {
      marginLeft: theme.spacing(2),
      height: theme.spacing(2),
      borderRadius: theme.spacing(2),
      whiteSpace: "nowrap",
    },
    toolbarSpacer: {
      height: theme.spacing(2),
    },
  });
};

export const formStyles = (theme: Theme) =>
  createStyles({
    formPadding: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  });

export const navStyles = (theme: Theme) =>
  createStyles({
    waffleIcon: {
      color: theme.palette.secondary.light,
    },
    waffleIconContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: theme.spacing(3),
      backgroundColor: theme.palette.primary.dark,
      ...theme.mixins.toolbar,
      minWidth: theme.mixins.toolbar.minHeight,
      "&:hover": {
        transition: "all 200ms ease-in",
        backgroundColor: theme.palette.primary.light,
      },
    },
    navToolbar: {
      paddingLeft: 0,
    },
    navContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      ...theme.mixins.toolbar,
    },
    navLink: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      ...theme.mixins.toolbar,
      color: theme.palette.secondary.main,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      "&:hover": {
        transition: "all 200ms ease-in",
        backgroundColor: theme.palette.primary.light,
      },
      textDecoration: "none",
      "&:visited": {
        color: theme.palette.secondary.main,
      },
    },
    navLinkSeparator: {
      borderLeftColor: theme.palette.secondary.main,
      borderLeft: "1px solid",
      opacity: 0.6,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      minHeight: theme.spacing(6),
    },
    logoContainer: { paddingLeft: "15px" },
    circleStyle: {
      width: "36px",
      color: "white",
      height: "36px",
      padding: "0",
      display: "inline-block",
      fontSize: "25px",
      textAlign: "center",
      borderRadius: "50%",
      backgroundColor: "rgb(118, 118, 118)",
      position: "absolute",
      top: "10px",
      right: "6px",
      fontFamily: "sans-serif",
      lineHeight: "35px",
      cursor: "pointer",
    },
    personaDetails: {
      whiteSpace: "nowrap",
      paddingLeft: "83px",
      paddingTop: "10px",
      fontFamily: "sans-serif",
      display: "table-cell",
    },
    drawerMenuContainer: {
      textAlign: "left",
      marginTop: "30px",
      lineHeight: "30px",
    },
    drawerMenuContainerLink: {
      fontFamily: "sans-serif",
      color: "#0072c6 !important",
      fontSize: "18px",
      textDecoration: "none",
      marginLeft: "20px",
      textTransform: "capitalize",
    },
  });

export const ToolbarWrapper: any = styled.div`
  position: ${(props: any) => (props.fixed ? "fixed" : "")};
  top: 64px;
  z-index: 9;
  background: ${(props: any) => (props.fixed ? "#ddd" : "")};
  width: 100%;
  left: 0;
  padding: ${(props: any) => (props.fixed ? "10px 0 0 30px" : "")};
  box-shadow: ${(props: any) =>
    props.fixed ? "-5px 4px 6px -4px rgba(0,0,0,0.62)" : "none"};
`;

export const CustomCaseHeader: any = styled.div`
  position: ${(props: any) => (props.fixed ? "fixed" : "absolute")};
  top: ${(props: any) => (props.fixed ? "98px" : "20px")};
  width: 100%;
  text-align: center;
  font-weight: bold;
  z-index: 99;
  left: 0;
`;

export const CustomCaseHeaderRoles: any = styled.div`
  position: ${(props: any) => (props.fixed ? "fixed" : "absolute")};
  top: ${(props: any) => (props.fixed ? "80px" : "5px")};
  width: 100%;
  text-align: center;
  font-weight: bold;
  z-index: 99;
  left: 0;
`;

export const FieldError: any = styled.span`
  position: absolute;
  right: 0;
  color: #cc0000;
  font-size: 14px;
`;

export const FieldTextAreaError: any = styled(FieldError)`
  && {
    right: 15px;
    top: 5px;
  }
`;

export const CustomButton: any = styled(Button)`
  && {
    z-index: 999;
  }
`;

export const styles = (theme: any) => ({
  inputLabel: {
    "&.shrink": {
      fontSize: "18px",
    },
  },
});

export const GridWrapper: any = styled.div`
  && {
    padding-top: 15px;
    position: relative;
    svg {
      color: ${PRIMARY_COLOR};
    }.k-grouping-header-flex {
      line-height: 35px;
  }
  .k-filter-row {
      // display: ${(props: any) => (props.isVisible ? "table-row" : "none")};
      input {
          border-width: 0 0 1px 0;
      }
      .k-filtercell {
          .k-input, .k-textbox {
              font-size: 14px;
          }
      }
  }
  .k-master-row {
      :hover{
          background: #efefef;
      }
      a {
          text-decoration: underline;
          color: #0000ff;
          :hover {
              cursor: pointer;
          }
      }
  }
  .k-grid-header-wrap .k-header .k-checkbox-label {
      // display: ${(props: any) =>
        props.isMultiSelectVisible ? "inline-block" : "none"};
      :hover {
          cursor: pointer;
      }
      :before {
          border-color: ${PRIMARY_COLOR}
      }
      :after {
          background-color: ${PrimayBackground};
          border-color: ${PrimayBackground};
      }
  }
  .k-grouping-row {
      input, label {
          display: none;
      }
  }
  .k-checkbox-label:before, .k-checkbox-label:after, .k-checkbox:focus+.k-checkbox-label:before {
      border-color: ${PRIMARY_COLOR};
  }
  .k-checkbox:checked+.k-checkbox-label:before, .k-checkbox:checked+.k-checkbox-label:after {
      background-color: ${PrimayBackground};
      border-color: ${PRIMARY_COLOR};
  }
  .k-pager-numbers .k-link.k-state-selected, .k-pager-numbers .k-link:link.k-state-selected {
      background-color: ${PrimayBackground};
  }
  .k-pager-sizes select {
      padding: 5px 10px;
  }
  .k-grid th {
      padding: 5px 24px 10px;
  }
  .k-grid td{
      padding: 0px 24px;
  }
  .k-grid .k-grouping-row .k-icon {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const GridItem = styled(Grid)`
&& {
    // height: 70px;
    position: relative;
    padding: ${(props: any) =>
      props.ismerge ? "4px 20px 10px 0 !important" : "4px 20px 10px !important"}
    padding-top: ${(props: any) =>
      props.ischeckbox ? "15px !important" : "4px !important"}
    // padding-right: ${(props: any) =>
      props.isPrint ? "0px !important" : "0px !important"};
    padding-right: 10px !important;
    padding-left: ${(props: any) =>
      props.isprint === "true" ? "0px !important" : "10px !important"}
    // max-height: 60px;
    > div {
        float: ${(props: any) => (props.ismerge ? "left" : "none")};
    }
    input {
        font-size: 14px;
        font-weight: bold;
        color: #333;
    }
    textarea {
        color: #333 !important;
        font-weight: bold;
        font-size: 14px;
    }
}
`;

export const GridContainer = styled(Grid)`
  && {
    margin: 0;
    width: 100%;
  }
`;

export const RecCountryGridITem: any = styled(Grid)`
    && {
        position: relative;
        padding: 10px !important;
        font-size:  ${(props: any) =>
          props.isheader === "true" ? "0.75rem" : "14px"};
        border-bottom: 1px solid rgba(224, 224, 224, 1);
        color: ${(props: any) =>
          props.isheader === "true" ? "rgba(0, 0, 0, 0.54)" : "auto"};
        font-weight: ${(props: any) =>
          props.isheader === "true" ? "500" : "auto"}
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        .nav-item {
            text-decoration: underline;
            &:hover {
                cursor: pointer;
            }
        }
    }
`;

export const LoaderContainer: any = styled.div`
  && {
    display: ${(props: any) => (props.visible ? "block" : "none")};
  }
`;

export const OverlayContainer: any = styled.div`
  && {
    width: 100%;
    height: ${(props: any) => (props.height ? props.height + "px" : "100%")};
    // height: 500px;
    background: #333;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.6;
    z-index: 999999;
  }
`;

export const PageProgress = styled(CircularProgress)`
  && {
    position: fixed;
    top: 50%;
    left: 48%;
    z-index: 11;
    color: #1c3342;
  }
`;

export const Close: any = styled(CloseIcon)`
  && {
    font-size: 16px;
  }
`;

export const StyledToaster: any = styled(Snackbar)`
  && {
    > div {
      color: #fff;
      line-height: 36px;
      max-width: none;
      .check-error {
        position: relative;
        .err-icon {
          position: absolute;
          top: 5px;
          left: -6px;
        }
        .msg {
          margin-left: 24px;
        }
      }
      /* button.close {
        top: 8px;
        position: absolute;
        right: 0;
      } */
    }
    @media only screen and (max-width: 420px) {
      width: 90%;
    }
  }
`;

export const StyledToasterError: any = styled(StyledToaster)`
  && {
    > div {
      background: ${TOASTER_ERROR};
      background-color: ${TOASTER_ERROR};
    }
  }
`;

export const StyledToasterSuccess: any = styled(StyledToaster)`
  && {
    > div {
      background: ${TOASTER_SUCCESS};
      background-color: ${TOASTER_SUCCESS};
    }
  }
`;

export const FormAutoSelectError = styled(FieldError)`
  bottom: 35px;
`;

export const A = styled.a`
  text-decoration: initial;
  color: rgba(0, 0, 0, 0.87);
  &:hover {
    color: rgba(0, 0, 0, 0.87);
  }
  &:vistied {
    color: rgba(0, 0, 0, 0.87);
  }
  &:active {
    color: rgba(0, 0, 0, 0.87);
  }
`;

export const ActionLogContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;


